import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import Breadcrumb from '../components/Breadcrumb';
import ResourceForm from '../components/ResourceForm';
import QueryError from '../components/QueryError';
import { UPDATE_PUBLICATION, CREATE_PUBLICATION } from '../graphql/mutations';
import { PUBLICATIONS_QUERY } from '../graphql/queries';

const PUBLICATION_QUERY = gql`
  query Publication($id: ID!) {
    publication(id: $id) {
      id
      title
      author
      year
      public
      license
    }
  }
`;

const LICENSE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'cc0', label: 'CC0 (Public Domain Dedication)' },
  { value: 'cc_by', label: 'CC-BY' },
  { value: 'cc_by_sa', label: 'CC-BY-SA' },
  { value: 'cc_by_nc', label: 'CC-BY-NC' },
  { value: 'cc_by_nd', label: 'CC-BY-ND (excluded from training)' },
  { value: 'public_domain', label: 'Public Domain Mark' },
  { value: 'all_rights_reserved', label: 'All Rights Reserved' }
];

const FORM_FIELDS = [
  { key: 'title', label: 'Title', type: 'text', required: true },
  { key: 'author', label: 'Author', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'text' },
  { key: 'license', label: 'License', type: 'select', options: LICENSE_OPTIONS },
  { key: 'public', label: 'Public', type: 'boolean' }
];

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PublicationFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data,
    loading: queryLoading,
    error: queryError
  } = useQuery(PUBLICATION_QUERY, {
    variables: { id },
    skip: !id
  });

  const [updatePub, { loading: updateLoading }] = useMutation(UPDATE_PUBLICATION, {
    refetchQueries: [{ query: PUBLICATIONS_QUERY }],
    awaitRefetchQueries: true
  });

  const [createPub, { loading: createLoading }] = useMutation(CREATE_PUBLICATION, {
    refetchQueries: [{ query: PUBLICATIONS_QUERY }],
    awaitRefetchQueries: true
  });

  const [pdfFile, setPdfFile] = React.useState(null);

  const loading = queryLoading || updateLoading || createLoading;
  const pub = data?.publication;

  const handleSubmit = async (formState) => {
    try {
      let variables = { ...formState };
      if (pdfFile) {
        variables.pdf = await readFileAsBase64(pdfFile);
      }
      if (!variables.license) delete variables.license;
      variables.public = Boolean(variables.public);
      if (id) {
        await updatePub({ variables: { input: { id, ...variables } } });
        navigate('/publications');
      } else {
        const result = await createPub({ variables: { input: variables } });
        const newId = result.data?.createPublication?.id;
        if (newId && pdfFile) {
          navigate(`/publications/${newId}/progress`);
        } else {
          navigate('/publications');
        }
      }
    } catch (err) {
      alert(err.message);
    }
  };

  if (queryLoading) return <div className="page-content">Loading...</div>;
  if (queryError)
    return (
      <div className="page-content">
        <QueryError error={queryError} />
      </div>
    );

  return (
    <div className="page-content">
      <Breadcrumb
        items={[
          { label: 'Publications', link: '/publications' },
          { label: id ? 'Edit Publication' : 'New Publication' }
        ]}
      />
      <ResourceForm
        resource={pub ? { ...pub, type: 'Publication' } : null}
        fields={FORM_FIELDS}
        onSubmit={handleSubmit}
        // onCancel={() => navigate(-1)}
        loading={loading}
        submitLabel={id ? 'Update Publication' : 'Upload Publication'}
      >
        {!id && (
          <div className="form-field">
            <label className="form-label" htmlFor="pdf">
              PDF
            </label>
            <input
              id="pdf"
              type="file"
              className="form-control"
              accept=".pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            />
          </div>
        )}
      </ResourceForm>
    </div>
  );
}
